// ── "Apply only when" gate: Code mode helpers (pure) ─────────────────────
// A gate is `apply_when` on a GATEABLE step (split_column so far):
//   builder: { conditions: [...], conjunction }                 (mode absent)
//   code:    { mode: "code", expr: {v:1, lang:"js", src, reviewed},
//              conditions?: [...] (dormant), conjunction? }
// The expression is ONE true/false condition per row: true = run the block
// for that row, false = pass it through. It never returns a value.
// `mode:"code"` is only ever written together with a non-empty source
// (Apply): the server treats a blank code gate as malformed. Switching back
// to Builder changes only `mode`; the code and the dropdown rows stay.
//
// A third, independent key sits alongside either mode: `stop_on_false` (bool,
// absent/false by default). Unset: a False row passes through unchanged (the
// behavior above). Set: ANY row failing the condition halts the whole
// pipeline run instead — see row_gate.py's check_halt / PipelineHaltError on
// the server. Orthogonal to builder vs. code, so it survives every mode
// switch; only `gateWithStopOnFalse` and an empty gate (nothing left to ever
// be false) touch it.

export function isCodeGate(aw) {
  return !!aw && aw.mode === 'code';
}

export function hasSavedGateCode(aw) {
  return typeof aw?.expr?.src === 'string' && aw.expr.src.trim() !== '';
}

// Written by Apply: the next `apply_when` value.
export function gateCodePatch(aw, src) {
  return { ...(aw || {}), mode: 'code', expr: { v: 1, lang: 'js', src, reviewed: true } };
}

// Back to the dropdown view: only the switch changes.
export function builderGate(aw) {
  return { ...(aw || {}), mode: 'builder' };
}

// Choosing "Code": the next `apply_when` to write now, or null when the
// choice must stay local UI state until Apply (nothing saved to switch to).
export function gateToggleWrite(aw) {
  return hasSavedGateCode(aw) ? { ...aw, mode: 'code' } : null;
}

export function needsGateReview(aw) {
  return isCodeGate(aw) && aw.expr?.reviewed === false;
}

// The builder rows changed: the next `apply_when` value, or null to remove
// the key. Saved code is never dropped (it stays dormant); a gate that holds
// code always carries an explicit mode, because the server refuses an
// expression with no mode and no conditions (it would match every row).
export function gateWithConditions(aw, conds, conjunction) {
  const base = aw || {};
  const hasCode = hasSavedGateCode(base);
  const conj = conjunction || base.conjunction || 'AND';
  if (!conds.length) {
    // Spread (not a hand-picked field list) so stop_on_false — or anything
    // else added later — survives emptying the builder rows while code is
    // still saved; dropping to null (no gate at all) when nothing is left
    // correctly drops it too, since there's then nothing to ever be false.
    return hasCode ? { ...base, mode: 'builder', conditions: [] } : null;
  }
  const next = { ...base, conditions: conds, conjunction: conj };
  if (hasCode) next.mode = 'builder';
  return next;
}

// Whether a False row should halt the whole pipeline run instead of just
// passing through unchanged (row_gate.py's check_halt on the server).
export function gateWantsHalt(aw) {
  return !!aw?.stop_on_false;
}

// The checkbox changed: the next `apply_when` value. Checking it with no
// gate content yet still writes the flag (harmless — the server's check_halt
// is a no-op without an actual condition/code to evaluate); unchecking it
// drops the key, and drops `apply_when` entirely if that was the only thing
// in it.
export function gateWithStopOnFalse(aw, stopOnFalse) {
  if (stopOnFalse) return { ...(aw || {}), stop_on_false: true };
  if (!aw) return null;
  const { stop_on_false: _drop, ...rest } = aw;
  return Object.keys(rest).length ? rest : null;
}

// What the section says about a gate on the canvas / in a summary.
export function gateSummary(aw) {
  const halt = gateWantsHalt(aw) ? ' · halts on False' : '';
  if (isCodeGate(aw)) return `Code${needsGateReview(aw) ? ' · needs review' : ''}${halt}`;
  const n = Array.isArray(aw?.conditions) ? aw.conditions.length : 0;
  return (n ? `${n} condition${n === 1 ? '' : 's'}` : 'every row') + halt;
}
